import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes, Navigate, useSearchParams } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { Widget } from './widget/Widget'
import type { Audience, Variant } from './types'
import './styles.css'

const AdminApp = lazy(() => import('./admin/AdminApp').then((m) => ({ default: m.AdminApp })))

/**
 * Embed iframe entrypoint. The embedding page passes the audience via
 * `?audience=unge|aeldre`; we default to `unge` if missing/invalid so legacy
 * embeds keep working.
 */
function EmbedSwitch() {
  const [params] = useSearchParams()
  const raw = params.get('audience')
  const audience: Audience = raw === 'aeldre' ? 'aeldre' : 'unge'
  return <Widget embed audience={audience} />
}

/**
 * Resolver variant for et audience-rod-besøg (/unge eller /aeldre uden eksplicit
 * chat/beregner). Bevidst forsøger vi at få det same A/B-split-resultat som
 * edge-middleware'n i functions/_middleware.ts ville have lavet — den kører
 * dog ikke i alle deploy-konfigurationer (afhænger af _redirects-precedence),
 * så vi har en SPA-side fallback her: cookie-låst variant > ?variant override
 * > deterministisk hash baseret på sessions-id eller en ny UUID.
 */
function resolveAudienceRootRedirect(audience: Audience): string {
  const params = new URLSearchParams(window.location.search)
  const search = window.location.search
  const queryRaw = params.get('variant')
  let variant: Variant | null = queryRaw === 'chat' || queryRaw === 'calculator' ? queryRaw : null
  if (!variant) {
    const m = document.cookie.match(/(?:^|;\s*)lf_variant=(chat|calculator)/)
    if (m) variant = m[1] as Variant
  }
  if (!variant) {
    // Deterministisk fallback når ingen cookie/override findes. Bruger
    // sessionsid-cookie hvis sat, ellers crypto.randomUUID. fnv1a sikrer
    // 50/50-fordeling — ikke perfect random men god nok til en A/B-split.
    const sid = document.cookie.match(/(?:^|;\s*)lf_session=([^;]+)/)?.[1] ?? crypto.randomUUID()
    let h = 2166136261
    for (let i = 0; i < sid.length; i++) {
      h ^= sid.charCodeAt(i)
      h = Math.imul(h, 16777619)
    }
    variant = (h >>> 0) % 2 === 0 ? 'chat' : 'calculator'
  }
  return variant === 'calculator'
    ? `/${audience}/beregner/${search}`
    : `/${audience}/chat${search}`
}

/**
 * Catch-all wildcard handler. Tidligere navigerede den blindt til /unge/chat
 * for ALLE ukendte paths, hvilket dræbte A/B-testen for direkte /unge- og
 * /aeldre-links. Nu sniffer vi den indkommende path: hvis det er en audience-
 * rod (/unge eller /aeldre), så A/B-split. Ellers default til /unge/chat
 * som før (brugeren landede på en typo eller død-link og skal et eller andet
 * sted hen).
 */
function CatchAllRedirect() {
  const path = window.location.pathname
  // Window.location er kilden til sandhed her — react-router-dom's hooks ville
  // give os "*" som matchet pattern, ikke det faktiske path. Vi laver en
  // window.location.replace istedet for <Navigate> så browser-history ikke
  // får en ekstra entry brugeren skal back-knappe gennem.
  if (path === '/unge' || path === '/unge/') {
    window.location.replace(resolveAudienceRootRedirect('unge'))
    return null
  }
  if (path === '/aeldre' || path === '/aeldre/') {
    window.location.replace(resolveAudienceRootRedirect('aeldre'))
    return null
  }
  return <Navigate to="/unge/chat" replace />
}

const root = createRoot(document.getElementById('root')!)

root.render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/unge/chat"   element={<Widget audience="unge" />} />
        <Route path="/aeldre/chat" element={<Widget audience="aeldre" />} />
        <Route path="/embed"       element={<EmbedSwitch />} />
        <Route path="/analytics/*" element={<Suspense fallback={null}><AdminApp /></Suspense>} />
        {/* Legacy redirects: old /admin URLs → /analytics so bookmarks survive */}
        <Route path="/admin"       element={<Navigate to="/analytics" replace />} />
        <Route path="/admin/*"     element={<Navigate to="/analytics" replace />} />
        <Route path="*"            element={<CatchAllRedirect />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
)
